// src/components/forms/FormTypeSelector.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Button,
    Typography,
    Box,
    Paper,
    alpha,
} from '@mui/material';
import {
    Assessment as EvaluationIcon,
    FeedbackOutlined as FeedbackIcon,
    TextFields as InputIcon,
    ArrowBack as BackIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import AdminLayout from './AdminLayout';

export default function FormTypeSelector() {
    const navigate = useNavigate();

    const formTypes = [
        {
            id: 'evaluation',
            title: 'Evaluation Form',
            description: 'Create comprehensive assessment forms with scoring and auto-evaluation capabilities.',
            icon: <EvaluationIcon sx={{ fontSize: 48 }} />,
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#667eea',
            features: [
                'Question-based scoring system',
                'Auto-reject options',
                'Multiple question types',
                'Hidden scoring logic',
            ],
        },
        {
            id: 'input-detail',
            title: 'Input Detail Form',
            description: 'Google Forms-style data collection with various field types for clean data gathering.',
            icon: <InputIcon sx={{ fontSize: 48 }} />,
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: '#f093fb',
            features: [
                'Multiple field types',
                'Custom placeholder text',
                'Optional/Required fields',
                'Clean data collection',
            ],
        },
        {
            id: 'feedback',
            title: 'Feedback Form',
            description: 'Collect user feedback, ratings, and testimonials with rating scales and text inputs.',
            icon: <FeedbackIcon sx={{ fontSize: 48 }} />,
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: '#4facfe',
            features: [
                'Rating scales (1-10)',
                'Text feedback fields',
                'Optional questions',
                'Simple feedback collection',
            ],
        },
    ];

    return (
        <AdminLayout title="Create Form">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/admin/forms')}
                        sx={{
                            mb: 3,
                            color: '#003366',
                            '&:hover': {
                                backgroundColor: alpha('#003366', 0.08),
                            }
                        }}
                    >
                        Back to Forms
                    </Button>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 3,
                            color: 'white',
                            mb: 4,
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                        >
                            🎨 Create a New Form
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                opacity: 0.95,
                                fontWeight: 400,
                            }}
                        >
                            Choose the type of form you want to create and start building
                        </Typography>
                    </Paper>
                </Box>

                {/* Form Type Cards */}
                <Grid container spacing={4}>
                    {formTypes.map((type) => (
                        <Grid item xs={12} md={4} key={type.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: `0 12px 24px ${alpha(type.color, 0.25)}`,
                                        borderColor: type.color,
                                    },
                                }}
                            >
                                {/* Icon Header */}
                                <Box
                                    sx={{
                                        p: 3,
                                        background: type.gradient,
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 120,
                                    }}
                                >
                                    {type.icon}
                                </Box>

                                {/* Content */}
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 2,
                                            color: '#1a1a1a',
                                        }}
                                    >
                                        {type.title}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 3, lineHeight: 1.7 }}
                                    >
                                        {type.description}
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1.5,
                                                color: type.color,
                                                textTransform: 'uppercase',
                                                fontSize: '0.75rem',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            Key Features
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {type.features.map((feature, idx) => (
                                                <Typography
                                                    key={idx}
                                                    component="li"
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.75,
                                                        color: 'text.secondary',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    {feature}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                </CardContent>

                                {/* Action Button */}
                                <Box sx={{ p: 3, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate(`/admin/forms/${type.id}/new`)}
                                        sx={{
                                            background: type.gradient,
                                            py: 1.5,
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: `0 4px 12px ${alpha(type.color, 0.3)}`,
                                            '&:hover': {
                                                boxShadow: `0 6px 20px ${alpha(type.color, 0.4)}`,
                                                transform: 'translateY(-2px)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        Create {type.title}
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Help Section */}
                <Paper
                    elevation={0}
                    sx={{
                        mt: 6,
                        p: 3,
                        background: alpha('#667eea', 0.05),
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha('#667eea', 0.2),
                    }}
                >
                    <Typography variant="body2" color="text.secondary" align="center">
                        💡 <strong>Tip:</strong> Not sure which form type to choose? Evaluation forms are best for assessments,
                        Input Detail forms for data collection, and Feedback forms for gathering user opinions.
                    </Typography>
                </Paper>
            </Container>
        </AdminLayout>
    );
}
